"use client"
import React, {
  createContext,
  ElementRef,
  forwardRef,
  useContext,
  useRef,
} from "react"
import { tv } from "tailwind-variants"
import { useCallbackRef, useDimensions } from "../../hooks"
import { classNames } from "../../utils/classNames"
import { AnimateHeight } from "../AnimateHeight"
import { FlexColumn } from "../FlexColumn"

export type FixedBottomSheetProps = {
  className?: string
  /**
   * This is the body content of the sheet.
   */
  children: React.ReactNode
  /**
   * This header is always present regardless of open state.
   */
  header?: React.ReactNode
  open?: boolean
}

const fixedBottomSheetVariants = tv({
  base: classNames(
    "fixed inset-x-0 bottom-0 z-fixed-bottom-sheet rounded-t-xl ease-out-quint bg-bg-app border border-border-1",
  ),
  variants: {
    open: {
      true: "translate-y-0",
    },
  },
})

const FixedBottomSheetContext = createContext<boolean>(false)

const FixedBottomSheetBase = forwardRef<
  ElementRef<"div">,
  FixedBottomSheetProps
>(function FixedBottomSheet(
  { children, className, header, open, ...rest },
  ref,
) {
  const initialOpen = useRef(open)
  const hasChangedOpen = useRef(false)
  const [headerRef, setHeaderRef] = useCallbackRef<HTMLDivElement>()
  const { height: headerHeight } = useDimensions(headerRef)

  if (open !== initialOpen.current) {
    initialOpen.current = open
    hasChangedOpen.current = true
  }

  return (
    <FixedBottomSheetContext.Provider value={Boolean(open)}>
      <div
        className={classNames(
          fixedBottomSheetVariants({ open }),
          // Only animate after the open state has changed.
          hasChangedOpen.current && "transition-transform duration-500",
          className,
        )}
        style={
          !open
            ? // This is so the header is always visible.
              { transform: `translateY(calc(100% - ${headerHeight}px))` }
            : undefined
        }
        {...rest}
        ref={ref}
      >
        {header && <div ref={setHeaderRef}>{header}</div>}
        <AnimateHeight>{children}</AnimateHeight>
      </div>
    </FixedBottomSheetContext.Provider>
  )
})

export const FixedBottomSheetBody = forwardRef<
  ElementRef<"div">,
  FixedBottomSheetProps
>(function FixedBottomSheetBody({ children, className, ...rest }, ref) {
  const open = useContext(FixedBottomSheetContext)
  return (
    <FlexColumn
      className={classNames(
        "p-4 pb-[calc(var(--ios-inset-bottom)+theme(spacing.4))]",
        className,
      )}
      ref={ref}
      // Prevents child focus. (inert is not populated in TS so that's why we're doing it like this)
      {...{ inert: open ? undefined : true }}
      {...rest}
    >
      {children}
    </FlexColumn>
  )
})

export const FixedBottomSheet = Object.assign(FixedBottomSheetBase, {
  Body: FixedBottomSheetBody,
})
